(() => {
  const linkSelector = '[data-islander-collection-link]';
  const navigationSelector = '[data-islander-collection-navigation]';
  const sectionKeys = ['banner', 'product-grid', 'category-navigation'];
  const pageCache = new Map();

  let activeRequest = null;
  let currentPathname = window.location.pathname;
  let transitionId = 0;

  const sectionSelector = (key) => `[id^="shopify-section-"][id$="__${key}"]`;

  function setBusy(isBusy) {
    document.documentElement.classList.toggle('islander-collection-transitioning', isBusy);
    const navigation = document.querySelector(navigationSelector);
    if (navigation) navigation.setAttribute('aria-busy', String(isBusy));
  }

  function syncFacetState(url) {
    const FacetFiltersForm = window.customElements?.get('facet-filters-form');
    if (!FacetFiltersForm) return;

    const searchParams = url.search.slice(1);
    FacetFiltersForm.searchParamsInitial = searchParams;
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.filterData = [];
  }

  function updateMetadata(nextDocument) {
    const nextTitle = nextDocument.querySelector('title');
    if (nextTitle?.textContent) document.title = nextTitle.textContent;

    ['link[rel="canonical"]', 'meta[name="description"]'].forEach((selector) => {
      const currentElement = document.head.querySelector(selector);
      const nextElement = nextDocument.head.querySelector(selector);
      if (currentElement && nextElement) currentElement.replaceWith(nextElement.cloneNode(true));
    });
  }

  function announceCollection() {
    const status = document.querySelector('[data-islander-collection-status]');
    const activeLink = document.querySelector(`${linkSelector}[aria-current="page"]`);
    if (!status || !activeLink) return;

    status.textContent = `${activeLink.textContent.trim()} products loaded.`;
  }

  async function fetchPage(url, signal) {
    const cacheKey = url.href;
    if (pageCache.has(cacheKey)) return pageCache.get(cacheKey);

    const response = await fetch(cacheKey, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    });
    if (!response.ok) throw new Error(`Collection request failed with ${response.status}.`);

    const html = await response.text();
    pageCache.set(cacheKey, html);
    if (pageCache.size > 8) pageCache.delete(pageCache.keys().next().value);
    return html;
  }

  async function navigate(targetUrl, { updateHistory, fallbackToNavigation }) {
    const requestId = ++transitionId;
    activeRequest?.abort();
    activeRequest = new AbortController();
    setBusy(true);
    syncFacetState(targetUrl);

    try {
      const html = await fetchPage(targetUrl, activeRequest.signal);
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const replacements = sectionKeys.map((key) => ({
        current: document.querySelector(sectionSelector(key)),
        next: nextDocument.querySelector(sectionSelector(key)),
      }));

      if (replacements.some(({ current, next }) => !current || !next)) {
        throw new Error('The collection page did not return every required section.');
      }
      if (requestId !== transitionId) return;

      if (updateHistory) {
        history.pushState(
          { islanderCollectionNavigation: true, searchParams: targetUrl.search.slice(1) },
          '',
          `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`,
        );
      }

      updateMetadata(nextDocument);
      replacements.forEach(({ current, next }) => current.replaceWith(document.importNode(next, true)));
      currentPathname = targetUrl.pathname;
      syncFacetState(targetUrl);

      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger();
      }

      announceCollection();
      document.dispatchEvent(new CustomEvent('islander:collection:changed', {
        detail: { url: targetUrl.href },
      }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (fallbackToNavigation) {
        window.location.assign(targetUrl.href);
      } else {
        window.location.reload();
      }
    } finally {
      if (requestId === transitionId) {
        activeRequest = null;
        setBusy(false);
      }
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest(linkSelector);
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (window.Shopify?.designMode) return;

    const targetUrl = new URL(link.href, window.location.href);
    if (targetUrl.origin !== window.location.origin) return;

    if (targetUrl.pathname === window.location.pathname && targetUrl.search === window.location.search) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    navigate(targetUrl, { updateHistory: true, fallbackToNavigation: true });
  });

  window.addEventListener('popstate', () => {
    if (window.location.pathname === currentPathname) return;

    const targetUrl = new URL(window.location.href);
    syncFacetState(targetUrl);
    navigate(targetUrl, { updateHistory: false, fallbackToNavigation: false });
  });
})();
